import { Component, inject } from '@angular/core';
import { CustomerPayload } from '../../models/customer.model';
import { CustomerService } from '../../services/customer.service';
import { catchError, delay, EMPTY, take, tap } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { AutoFocusModule } from 'primeng/autofocus';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';
import { NgIf } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-add-customer',
    imports: [
        ToastModule,
        AutoFocusModule,
        ButtonModule,
        CalendarModule,
        CheckboxModule,
        DividerModule,
        InputNumberModule,
        InputTextModule,
        PaginatorModule,
        ReactiveFormsModule,
        RouterLink,
        NgIf,
        DatePicker,
    ],
    templateUrl: './add-customer.component.html',
    styleUrl: './add-customer.component.scss',
})
export class AddCustomerComponent {
    fb: FormBuilder = inject(FormBuilder);
    customerService = inject(CustomerService);
    messageService = inject(MessageService);
    pageName = 'عميل';
    routeName = 'customers';
    router = inject(Router);
    customForm: FormGroup;
    maxDate: Date = new Date();
    btnSaved = false;
    validDate: string;

    constructor() {
        this.createForm();
    }

    createForm(): void {
        this.customForm = this.fb.group({
            code: [''],
            name: ['', Validators.required],
            email: [null, Validators.email],
            openingBalance: [0],
            openingBalanceDate: [new Date()],
            phoneNumber: [null, [Validators.minLength(10), Validators.maxLength(25)]],
            vatNumber: [null, [Validators.minLength(7), Validators.maxLength(30)]],
            commercialRegister: [null, [Validators.maxLength(50)]],
            address: [null],
            isB2B: [false],
            buildingNumber: [null, [Validators.maxLength(5)]],
            additionalNumber: [null, [Validators.maxLength(7)]],
            streetName: [null, [Validators.maxLength(50)]],
            city: [null, [Validators.maxLength(30)]],
            district: [null, [Validators.maxLength(30)]],
            zip: [null, [Validators.min(11111), Validators.max(99999)]],
            countryKey: ['SA', [Validators.minLength(2), Validators.maxLength(2)]],
        });
        this.customForm.get('isB2B').valueChanges
            .pipe(
                takeUntilDestroyed(),
                tap((isB2B: boolean) => {
                    if (isB2B) {
                        this.customForm.get('vatNumber').addValidators(Validators.required);
                        this.customForm.get('commercialRegister').addValidators(Validators.required);
                        this.customForm.get('buildingNumber').addValidators(Validators.required);
                        this.customForm.get('additionalNumber').addValidators(Validators.required);
                        this.customForm.get('streetName').addValidators(Validators.required);
                        this.customForm.get('city').addValidators(Validators.required);
                        this.customForm.get('district').addValidators(Validators.required);
                        this.customForm.get('zip').addValidators(Validators.required);
                    } else {
                        this.customForm.get('vatNumber').removeValidators(Validators.required);
                        this.customForm.get('commercialRegister').removeValidators(Validators.required);
                        this.customForm.get('buildingNumber').removeValidators(Validators.required);
                        this.customForm.get('additionalNumber').removeValidators(Validators.required);
                        this.customForm.get('streetName').removeValidators(Validators.required);
                        this.customForm.get('city').removeValidators(Validators.required);
                        this.customForm.get('district').removeValidators(Validators.required);
                        this.customForm.get('zip').removeValidators(Validators.required);
                    }
                }),
            ).subscribe();
    }

    addNewCustomer(customerPayload: CustomerPayload) {
        customerPayload.email = customerPayload.email || null;
        customerPayload.code = undefined;
        this.customerService
            .addCustomer(customerPayload)
            .pipe(
                take(1),
                tap(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: `تم إضافه ${this.pageName} بنجاح `,
                    });
                }),
                delay(200),
                tap(() => {
                    this.router.navigate([this.routeName]);
                }),
                catchError(() => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'حدث خطأ ما، الرجاء اعاده المحاوله',
                    });
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    save(): void {
        if (this.customForm.valid) {
            const formDate = {
                ...this.customForm.value,
                openingBalanceDate: this.validDate,
            };
            this.addNewCustomer(formDate);
            this.btnSaved = true;
        }
    }

    onDateSelect($event: Date) {
        this.validDate = $event.toISOString().substring(0, 10);
    }

    selectOnFocus(e: Event) {
        if ((e.target as HTMLInputElement).select) {
            (e.target as HTMLInputElement).select();
        }
    }
}
